import { useEffect, useRef, useState } from "react";
import { Clock, Minimize2, Pause, Play, Square } from "lucide-react";
import { useReviewMode } from "../../context/ReviewModeContext";
import CommentDialog from "./CommentDialog";
import {
  usePerformanceTracker,
  usePerformanceTrackerOptional,
} from "./PerformanceTrackerContext";
import { useTrackerChromeOptional } from "./trackerChrome";
import "./PerformanceTrackerWidget.css";

const STATUS_LABEL = {
  NOT_STARTED: "Not started",
  RUNNING: "Running",
  PAUSED: "Paused",
  AUTO_PAUSED: "Inactive",
  STOPPED: "Stopped",
};

const HEADER_ICON = {
  NOT_STARTED: Clock,
  RUNNING: Play,
  PAUSED: Pause,
  AUTO_PAUSED: Pause,
  STOPPED: Square,
};

const COLLAPSE_DELAY_MS = 5000;

function splitActiveTime(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return {
    hh: String(hours).padStart(2, "0"),
    mm: String(minutes).padStart(2, "0"),
    ss: String(rest).padStart(2, "0"),
    label: [hours, minutes, rest].map((part) => String(part).padStart(2, "0")).join(":"),
  };
}

/** Display-only clock. Reported time always comes from the server payload. */
function useDisplayedActiveSeconds(status, activeSeconds, serverTime) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (status !== "RUNNING") return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [status, serverTime]);

  if (status !== "RUNNING" || !serverTime) {
    return activeSeconds || 0;
  }
  const elapsed = Math.max(0, Math.floor((now - Date.parse(serverTime)) / 1000));
  return (activeSeconds || 0) + elapsed;
}

export function PerformanceTrackerCompact() {
  const { reviewMode } = useReviewMode();
  const tracker = usePerformanceTrackerOptional();
  const chrome = useTrackerChromeOptional();

  if (reviewMode || !tracker || !chrome || tracker.enabled === false) {
    return null;
  }

  const status = tracker.status || "NOT_STARTED";
  const CompactIcon = HEADER_ICON[status] || Play;

  return (
    <button
      type="button"
      className={`pat-compact pat-compact--header pat-compact--${status.toLowerCase()}`}
      aria-label={`${STATUS_LABEL[status] || status}. ${chrome.expanded ? "Collapse" : "Expand"} work tracker`}
      aria-expanded={chrome.expanded}
      onClick={chrome.toggle}
    >
      <CompactIcon size={18} />
    </button>
  );
}

export default function PerformanceTrackerWidget() {
  const { reviewMode } = useReviewMode();
  const tracker = usePerformanceTracker();
  const chrome = useTrackerChromeOptional();
  const displayedSeconds = useDisplayedActiveSeconds(
    tracker.status,
    tracker.activeSeconds,
    tracker.serverTime,
  );
  const [isPointerOver, setIsPointerOver] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const collapseTimerRef = useRef(null);

  const status = tracker.status || "NOT_STARTED";
  const expanded = Boolean(chrome?.expanded);
  const dialogOpen = tracker.dialog === "pause" || tracker.dialog === "stop";
  const keepExpanded = isPointerOver || isFocusWithin || dialogOpen || tracker.busy;

  useEffect(() => {
    if (!chrome) return undefined;
    if (!expanded || keepExpanded) {
      if (collapseTimerRef.current) {
        window.clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      return undefined;
    }

    collapseTimerRef.current = window.setTimeout(() => {
      chrome.collapse();
    }, COLLAPSE_DELAY_MS);

    return () => {
      if (collapseTimerRef.current) {
        window.clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [chrome, expanded, keepExpanded]);

  if (reviewMode || tracker.enabled === false || !chrome) {
    return null;
  }

  const HeaderIcon = HEADER_ICON[status] || Clock;
  const statusClass = status.toLowerCase();
  const time = splitActiveTime(displayedSeconds);
  const showSessionBadge = status !== "NOT_STARTED";
  const accentSeconds = status === "RUNNING";

  const collapse = (event) => {
    event?.currentTarget?.blur();
    setIsFocusWithin(false);
    chrome.collapse();
  };

  return (
    <>
      <div
        className={`pat-shell ${expanded ? "pat-shell--expanded" : "pat-shell--compact"}`}
        onPointerEnter={() => setIsPointerOver(true)}
        onPointerLeave={() => setIsPointerOver(false)}
        onFocusCapture={() => setIsFocusWithin(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsFocusWithin(false);
          }
        }}
      >
        <aside
          className={`pat-widget pat-widget--${statusClass}`}
          aria-label="Performance activity tracker"
          aria-hidden={!expanded}
        >
          <div className="pat-widget__header">
            <span className={`pat-widget__icon pat-widget__icon--${statusClass}`} aria-hidden="true">
              <HeaderIcon size={12} />
            </span>
            <div className="pat-widget__titles">
              <span className="pat-widget__kicker">Work tracker</span>
              <span className={`pat-widget__status pat-widget__status--${statusClass}`}>
                {status === "RUNNING" ? <span className="pat-widget__dot" /> : null}
                {STATUS_LABEL[status] || status}
              </span>
            </div>
            {showSessionBadge ? <span className="pat-widget__badge">Session</span> : null}
            <button
              type="button"
              className="pat-widget__collapse"
              aria-label="Collapse work tracker"
              tabIndex={expanded ? 0 : -1}
              onClick={collapse}
            >
              <Minimize2 size={12} />
            </button>
          </div>

          <div className="pat-widget__clock" aria-live="polite" aria-label={time.label}>
            <div className="pat-widget__unit">
              <span className="pat-widget__digits">{time.hh}</span>
              <span className="pat-widget__unit-label">HH</span>
            </div>
            <span className="pat-widget__colon" aria-hidden="true" />
            <div className="pat-widget__unit">
              <span className="pat-widget__digits">{time.mm}</span>
              <span className="pat-widget__unit-label">MM</span>
            </div>
            <span className="pat-widget__colon" aria-hidden="true" />
            <div className="pat-widget__unit">
              <span className={`pat-widget__digits${accentSeconds ? " pat-widget__digits--accent" : ""}`}>
                {time.ss}
              </span>
              <span className="pat-widget__unit-label">SS</span>
            </div>
          </div>

          <div className="pat-widget__footer">
            {status === "STOPPED" ? (
              <p className="pat-widget__last">Last session: {time.label}</p>
            ) : null}
            {status === "AUTO_PAUSED" ? (
              <p className="pat-widget__last">Paused after inactivity</p>
            ) : null}
            {tracker.error ? <p className="pat-widget__error">{tracker.error}</p> : null}

            <div className="pat-widget__toolbar">
              {status === "RUNNING" ? (
                <span className="pat-widget__eq" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <span className="pat-widget__eq-spacer" />
              )}
              <div className="pat-widget__actions">
              {status === "NOT_STARTED" && (
                <button
                  type="button"
                  className="pat-btn pat-btn--launch"
                  onClick={() => tracker.start().catch(() => {})}
                  disabled={tracker.busy}
                  tabIndex={expanded ? 0 : -1}
                >
                  <Play size={12} />
                  Start tracking
                </button>
              )}
              {status === "STOPPED" && (
                <button
                  type="button"
                  className="pat-btn pat-btn--launch"
                  onClick={() => tracker.start().catch(() => {})}
                  disabled={tracker.busy}
                  tabIndex={expanded ? 0 : -1}
                >
                  <Play size={12} />
                  Start new session
                </button>
              )}
              {status === "RUNNING" && (
                <button
                  type="button"
                  className="pat-btn pat-btn--ghost-dark"
                  onClick={() => tracker.setDialog("pause")}
                  disabled={tracker.busy}
                  tabIndex={expanded ? 0 : -1}
                >
                  <Pause size={12} />
                  Pause
                </button>
              )}
              {status === "PAUSED" && (
                <button
                  type="button"
                  className="pat-btn pat-btn--resume"
                  onClick={() => tracker.resume().catch(() => {})}
                  disabled={tracker.busy}
                  tabIndex={expanded ? 0 : -1}
                >
                  <Play size={12} />
                  Resume
                </button>
              )}
              {(status === "RUNNING" || status === "PAUSED" || status === "AUTO_PAUSED") && (
                <button
                  type="button"
                  className="pat-btn pat-btn--stop"
                  onClick={() => tracker.setDialog("stop")}
                  disabled={tracker.busy}
                  tabIndex={expanded ? 0 : -1}
                >
                  <Square size={10} />
                  Stop
                </button>
              )}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {tracker.dialog === "pause" ? (
        <CommentDialog
          title="Pause tracking"
          description="Add a reason before pausing. Mouse or keyboard activity will not resume this session."
          confirmLabel="Pause"
          tone="pause"
          required={tracker.requirePauseComment !== false}
          busy={tracker.busy}
          onCancel={() => tracker.setDialog(null)}
          onConfirm={(comment) => tracker.pause(comment)}
        />
      ) : null}

      {tracker.dialog === "stop" ? (
        <CommentDialog
          title="Stop tracking"
          description="This ends the current session. Start later creates a new session."
          confirmLabel="Stop session"
          tone="stop"
          required={tracker.requireStopComment !== false}
          busy={tracker.busy}
          onCancel={() => tracker.setDialog(null)}
          onConfirm={(comment) => tracker.stop(comment)}
        />
      ) : null}
    </>
  );
}
